import React, { useEffect, Dispatch, SetStateAction, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { get, noop } from "lodash";
import { RaceProgram } from "@tvg/ts-types/Race";
import { NullaryFn } from "@tvg/ts-types/Functional";
import RaceHeaderUtils from "@tvg/utils/RaceHeaderUtils";
import {
  getAccountNumber,
  getIsLogged,
  getBalance
} from "@urp/store-selectors";
import { Container, FixedBannerContainer } from "./styled-components";
import useIsFavorite from "../../hooks/useIsFavorite";
import {
  updateFavorites,
  fetchUserFavoriteTracks
} from "../../utils/userFavoriteUtils";
import RaceSelector from "./RaceSelector";
import TrackHeaderLoading from "../loadings/TrackHeaderLoading";
import HeaderWrapper from "./HeaderWrapper";
import SubHeaderWrapper from "./SubHeaderWrapper";
import { isFdr } from "../../utils/general";
import { setIsPoolsNInfoModalOpen } from "../../store/actions";
import { poolsNInfoModalOpenEvent } from "../../utils/gtm/poolsAndInfo";

interface TrackHeaderProps {
  race: RaceProgram;
  races: RaceProgram[];
  isLoading: boolean;
  prevPath?: string;
  setIsMtpHidden: Dispatch<SetStateAction<boolean>>;
  onAccountBalanceClick?: NullaryFn<void>;
  openLogin?: NullaryFn<void>;
  openRegister?: NullaryFn<void>;
  onClickVideo?: NullaryFn<void>;
  isAccountCompliant?: boolean;
  withFixedBanner?: boolean;
  refetchBettingInterests?: NullaryFn<void>;
  isMobile?: boolean;
  fixedBanner?: React.ReactElement;
  withVideo?: boolean;
}

const TrackHeader = ({
  race,
  races,
  isLoading,
  setIsMtpHidden,
  prevPath = "/home",
  onAccountBalanceClick = noop,
  openLogin = noop,
  openRegister = noop,
  onClickVideo = noop,
  withFixedBanner = false,
  refetchBettingInterests = noop,
  isMobile = false,
  fixedBanner,
  withVideo,
  isAccountCompliant
}: TrackHeaderProps) => {
  const dispatch = useDispatch();
  const [finalPrevPath, setFinalPrevPath] = useState(prevPath);
  const [isFavoriteLoading, setIsFavoriteLoading] = useState(false);
  const firstRace = get(races, "[0]");
  const lastRace = races.find((_, index, { length }) => index + 1 === length);
  const country = get(race, "track.trackLocation.country");
  const trackName = get(race, "track.trackName");
  const mtp = get(race, "mtp", "0");
  const status = get(race, "status.code");
  const postTime = get(race, "postTime");
  const trackCode = get(race, "track.trackCode");
  const isLogged = useSelector(getIsLogged);
  const accountNumber = useSelector(getAccountNumber);
  const accountBalance = useSelector(getBalance);
  const favoriteTracks = useSelector((store) =>
    get(store, "userFavorites.tracksWithId")
  );
  const { isFavorite, setIsFavorite, favoriteId } = useIsFavorite(
    trackCode,
    favoriteTracks,
    isLogged
  );

  const videoFeedback = RaceHeaderUtils.getVideoFeedback(
    race,
    firstRace,
    lastRace,
    false
  );

  useEffect(() => {
    if (typeof window !== "undefined") {
      fetchUserFavoriteTracks(accountNumber, dispatch);
    }
  }, []);

  useEffect(() => {
    if (!prevPath.includes("/racetracks") && prevPath !== " ") {
      setFinalPrevPath(prevPath);
    }
  }, [prevPath]);

  if (isLoading) {
    return <TrackHeaderLoading isMobile={isMobile} />;
  }

  const onClickFavorite = () => {
    setIsFavoriteLoading(true);
    updateFavorites({
      dispatch,
      isAddingFavorite: !isFavorite,
      trackCode,
      setIsFavorite,
      isLogged,
      accountNumber,
      favoriteId,
      isAccountCompliant,
      onFinish: () => setIsFavoriteLoading(false)
    });
  };

  const baseSubHeaderProps = {
    races,
    race,
    country,
    title: trackName,
    isFavorite,
    isLogged,
    onClickFavorite,
    videoFeedback
  };

  return (
    <>
      <HeaderWrapper
        prevPath={finalPrevPath}
        race={race}
        balance={accountBalance}
        isLogged={isLogged}
        onAccBalanceClick={onAccountBalanceClick}
        openLogin={openLogin}
        openRegister={openRegister}
        mtp={+mtp}
        status={status}
        postTime={postTime as string}
        setIsMtpHidden={setIsMtpHidden}
        isMobile={isMobile}
        onClickVideo={onClickVideo}
        onClickFavorite={onClickFavorite}
        videoFeedback={videoFeedback}
        title={trackName}
        withVideo={withVideo}
      />
      {withFixedBanner && isMobile && (
        <FixedBannerContainer data-qa-label="track-header-fixed-banner">
          {fixedBanner}
        </FixedBannerContainer>
      )}
      <Container
        data-qa-label="track-header"
        id="track-header-ref"
        isFDR={isFdr()}
      >
        {!withVideo && (
          <SubHeaderWrapper
            {...baseSubHeaderProps}
            mtp={+mtp}
            status={status}
            postTime={postTime as string}
            setIsMtpHidden={setIsMtpHidden}
            onClickVideo={onClickVideo}
            onClickPoolsNInfo={() => {
              refetchBettingInterests();
              dispatch(
                setIsPoolsNInfoModalOpen({
                  isPoolsNInfoModalOpen: true,
                  PIRace: race
                })
              );
              poolsNInfoModalOpenEvent();
            }}
            isMobile={isMobile}
            isFavoriteLoading={isFavoriteLoading}
          />
        )}
        {!isMobile && !withVideo && <RaceSelector races={races} />}
      </Container>
    </>
  );
};

export default TrackHeader;
